import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import {
  FulfillmentLifecycleStatus,
  FulfillmentLineExceptionType,
  FulfillmentWaveOrderStatus,
  FulfillmentWaveStatus,
  FulfillmentWaveType,
  WaveAdminDetail,
  WaveListItem,
  WaveResponse,
} from './types';

export async function createWave(
  type: FulfillmentWaveType,
  shopifyOrderGids: string[],
): Promise<WaveResponse> {
  const res = await axios.post(`${API_URL}/fulfillment/waves`, { type, shopifyOrderGids });
  return (res as AxiosResponse<WaveResponse>).data;
}

export async function getActiveWave(): Promise<WaveResponse | null> {
  const res = await axios.get(`${API_URL}/fulfillment/waves/active`);
  return (res as AxiosResponse<WaveResponse | null>).data;
}

export async function getWave(waveId: number): Promise<WaveResponse | null> {
  const res = await axios.get(`${API_URL}/fulfillment/waves/${waveId}`);
  return (res as AxiosResponse<WaveResponse | null>).data;
}

export async function cancelWave(waveId: number): Promise<void> {
  await axios.post(`${API_URL}/fulfillment/waves/${waveId}/cancel`);
}

export async function scanPick(
  waveId: number,
  lineItemGid: string,
  scannedSku: string,
): Promise<{ success: true } | { error: string }> {
  const encoded = encodeURIComponent(lineItemGid);
  const res = await axios.post(
    `${API_URL}/fulfillment/waves/${waveId}/items/${encoded}/scan-pick`,
    { scannedSku },
  );
  return (res as AxiosResponse<{ success: true } | { error: string }>).data;
}

export async function scanQc(
  waveId: number,
  lineItemGid: string,
  binCode: string,
  fromBinCode?: string,
  quantity?: number,
  isFinalSegment?: boolean,
): Promise<WaveResponse | { error: string }> {
  const encoded = encodeURIComponent(lineItemGid);
  const res = await axios.post(`${API_URL}/fulfillment/waves/${waveId}/items/${encoded}/scan-qc`, {
    binCode,
    fromBinCode,
    quantity,
    isFinalSegment,
  });
  return (res as AxiosResponse<WaveResponse | { error: string }>).data;
}

export async function reportPickException(
  waveId: number,
  lineItemGid: string,
  exceptionType: FulfillmentLineExceptionType,
  notes: string | null,
): Promise<WaveResponse | { error: string }> {
  const encoded = encodeURIComponent(lineItemGid);
  const res = await axios.post(
    `${API_URL}/fulfillment/waves/${waveId}/items/${encoded}/exception`,
    {
      exceptionType,
      notes,
    },
  );
  return (res as AxiosResponse<WaveResponse | { error: string }>).data;
}

export async function bulkSetOrderStatus(
  shopifyOrderGids: string[],
  status: FulfillmentLifecycleStatus,
): Promise<void> {
  await axios.patch(`${API_URL}/fulfillment/orders/bulk-status`, { shopifyOrderGids, status });
}

export async function setOrderStatus(
  shopifyOrderId: number,
  status: FulfillmentLifecycleStatus,
): Promise<void> {
  await axios.patch(`${API_URL}/fulfillment/orders/${shopifyOrderId}/status`, { status });
}

export async function completeWave(waveId: number): Promise<void> {
  await axios.post(`${API_URL}/fulfillment/waves/${waveId}/complete`);
}

export async function updateWaveStatus(
  waveId: number,
  status: FulfillmentWaveStatus,
): Promise<void> {
  await axios.patch(`${API_URL}/fulfillment/waves/${waveId}`, { status });
}

export async function updateWaveOrderStatus(
  waveOrderId: number,
  status: FulfillmentWaveOrderStatus,
): Promise<void> {
  await axios.patch(`${API_URL}/fulfillment/wave-orders/${waveOrderId}`, { status });
}

export async function listWaves(status?: FulfillmentWaveStatus): Promise<WaveListItem[]> {
  const params = status ? `?status=${status}` : '';
  const res = await axios.get(`${API_URL}/fulfillment/waves${params}`);
  return (res as AxiosResponse<WaveListItem[]>).data;
}

export async function getWaveAdminDetail(waveId: number): Promise<WaveAdminDetail | null> {
  const res = await axios.get(`${API_URL}/fulfillment/waves/${waveId}/admin`);
  return (res as AxiosResponse<WaveAdminDetail | null>).data;
}
